import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ARTICLES, DOC_GROUPS, articleById, type DocArticle, type DocBlock } from '../content/docs-content'
import {
  Alert,
  Article,
  ArticleLayout,
  ArticleNavGroup,
  ArticleNavLink,
  ArticlePager,
  Button,
  List,
  Menu,
  type MenuItem,
  SearchField,
} from '../ds'
import { useBreakpoint } from '../shell/useBreakpoint'

/**
 * Docs — the design system's Article layout (D-36: `ArticleLayout` › nav + main › `Article`,
 * body `mv-prose--article`). Every article is a real link
 * (`?a=<id>`), so it is focusable and opens in a new tab; below 900px the nav becomes a row of
 * search plus a "Contents" menu rather than disappearing.
 */

// Minimal inline markup: **bold**, _serif-italic emphasis_, `code`.
const INLINE = /(\*\*[^*]+\*\*|_[^_]+_|`[^`]+`)/g
function inline(text: string) {
  return text.split(INLINE).filter(Boolean).map((tok, i) => {
    if (tok.startsWith('**') && tok.endsWith('**')) return <b key={i}>{tok.slice(2, -2)}</b>
    if (tok.startsWith('_') && tok.endsWith('_'))
      return (
        <span className="mv-em" key={i}>
          {tok.slice(1, -1)}
        </span>
      )
    if (tok.startsWith('`') && tok.endsWith('`')) return <code key={i}>{tok.slice(1, -1)}</code>
    return <span key={i}>{tok}</span>
  })
}

function Block({ block }: { block: DocBlock }) {
  switch (block.type) {
    case 'h2':
      return <h2>{block.text}</h2>
    case 'p':
      return <p>{inline(block.text)}</p>
    case 'list':
      return (
        <List>
          {block.items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </List>
      )
    case 'callout':
      return (
        <Alert tone="attention" orb>
          {inline(block.text)}
        </Alert>
      )
    case 'code':
      return (
        <pre>
          <code>{block.text}</code>
        </pre>
      )
    default:
      return null
  }
}

const hrefOf = (id: string) => `?a=${encodeURIComponent(id)}`

/** Everything an article says — title, lede and every block — lower-cased once, for the search box. */
function textOf(a: DocArticle): string {
  const blocks = a.blocks.map((b) => (b.type === 'list' ? b.items.join(' ') : b.text))
  return [a.title, a.lede, ...blocks].join(' ').toLowerCase()
}
const SEARCH_TEXT = new Map(ARTICLES.map((a) => [a.id, textOf(a)]))

/** A plain left click stays in the app; a modified one (new tab, new window) is the browser's. */
function isPlainClick(e: MouseEvent) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}

export default function Docs() {
  const [params, setParams] = useSearchParams()
  const initial = params.get('a')
  const [activeId, setActiveId] = useState(initial && articleById(initial) ? initial : ARTICLES[0].id)
  const [query, setQuery] = useState('')
  const narrow = useBreakpoint() === 'narrow'
  const mainRef = useRef<HTMLElement>(null)

  const active: DocArticle = articleById(activeId) || ARTICLES[0]
  const flatIndex = ARTICLES.findIndex((a) => a.id === active.id)
  const prev = flatIndex > 0 ? ARTICLES[flatIndex - 1] : null
  const next = flatIndex < ARTICLES.length - 1 ? ARTICLES[flatIndex + 1] : null

  const select = (id: string) => {
    setActiveId(id)
    setParams({ a: id }, { replace: true })
  }
  // A new article starts at its top.
  useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 })
  }, [activeId])
  const follow = (id: string) => (e: MouseEvent) => {
    if (!isPlainClick(e)) return
    e.preventDefault()
    select(id)
  }

  // Sidebar grouped + filtered by the search box — over the whole article, not only its title
  // and lede: "discount" finds the pages that discuss discounts (MAR-412).
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase()
    return DOC_GROUPS.map((g) => ({
      label: g,
      items: ARTICLES.filter((a) => a.group === g && (!q || (SEARCH_TEXT.get(a.id) ?? '').includes(q))),
    })).filter((g) => g.items.length > 0)
  }, [query])
  const matches = groups.reduce((n, g) => n + g.items.length, 0)

  const search = (
    <SearchField
      value={query}
      onChange={setQuery}
      label="Search the docs"
      placeholder="Search the docs…"
      resultsText={query.trim() ? `${matches} article${matches === 1 ? '' : 's'}` : undefined}
    />
  )

  const contents: MenuItem[] = groups.flatMap((g) => [
    { type: 'label' as const, label: g.label },
    ...g.items.map((a) => ({ key: a.id, label: a.title, checked: a.id === active.id, onSelect: () => select(a.id) })),
  ])

  // The layout decides which nav it shows: it measures its own width (beside the Ask dock the
  // canvas is ~500px on a laptop) and the viewport's narrow band forces it compact too.
  const compactNav = (
    <>
      {search}
      <Menu
        label="Contents"
        placement="bottom-end"
        items={contents.length > 0 ? contents : [{ type: 'label', label: 'No matches.' }]}
        trigger={
          <Button variant="ghost" size="sm">
            Contents
          </Button>
        }
      />
    </>
  )
  const nav = (
    <>
      {search}
      {groups.map((g) => (
        <ArticleNavGroup key={g.label} label={g.label}>
          {g.items.map((a) => (
            <ArticleNavLink key={a.id} href={hrefOf(a.id)} current={a.id === active.id} onClick={follow(a.id)}>
              {a.title}
            </ArticleNavLink>
          ))}
        </ArticleNavGroup>
      ))}
      {/* In a group of its own, so it sits where the first group would — off the search field,
          at the nav's reading size rather than the 11px hint (MAR-412). */}
      {groups.length === 0 && (
        <ArticleNavGroup>
          <p className="t-small">No matches.</p>
        </ArticleNavGroup>
      )}
    </>
  )

  return (
    <ArticleLayout nav={nav} compactNav={compactNav} compact={narrow} navLabel="Docs" mainRef={mainRef}>
      <Article
        breadcrumb={
          <>
            {active.group} / {active.title}
          </>
        }
        title={active.title}
        lede={inline(active.lede)}
      >
        <div className="mv-prose mv-prose--article">
          {active.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        <ArticlePager
          prev={prev ? { title: prev.title, href: hrefOf(prev.id), onClick: follow(prev.id) } : null}
          next={next ? { title: next.title, href: hrefOf(next.id), onClick: follow(next.id) } : null}
        />
      </Article>
    </ArticleLayout>
  )
}
